import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';
import { countStats } from '../utils/bookUtils';
import StatsBar from './StatsBar';
import PartCard from './PartCard';

const { FiFileText } = FiIcons;

export default function BookPreview({ parts, onRemovePart, onReorderParts, bookTitle, bookAuthor }) {
  const [viewMode, setViewMode] = useState('preview');
  const stats = useMemo(() => countStats(parts), [parts]);

  const combinedMarkdown = useMemo(() => {
    const lines = [];
    if (bookTitle) {
      lines.push(`# ${bookTitle}`);
      if (bookAuthor) lines.push(`\n*by ${bookAuthor}*`);
      lines.push('\n---\n');
    }
    parts.forEach(p => lines.push(p.content));
    return lines.join('\n\n---\n\n');
  }, [parts, bookTitle, bookAuthor]);

  const moveUp = (index) => {
    if (index === 0) return;
    const updated = [...parts];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onReorderParts(updated);
  };

  const moveDown = (index) => {
    if (index === parts.length - 1) return;
    const updated = [...parts];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onReorderParts(updated);
  };

  if (parts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-400 bg-white rounded-xl border border-dashed border-slate-300 p-8">
        <div className="bg-indigo-50 p-6 rounded-2xl mb-5">
          <SafeIcon icon={FiFileText} className="text-5xl text-indigo-300" />
        </div>
        <h3 className="text-base font-black text-slate-700">Your book is empty</h3>
        <p className="text-sm text-slate-500 mt-2 text-center max-w-xs leading-relaxed">
          Use a template or paste content in the Drafting Pad, then click <strong>"Add Part to Book"</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Header — fixed, never scrolls */}
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex items-center justify-between shrink-0">
        <h2 className="text-xs font-black text-slate-600 uppercase tracking-widest flex items-center">
          <SafeIcon icon={FiFileText} className="mr-2 text-indigo-500" />
          Book Preview
        </h2>
        <div className="flex bg-slate-200 p-1 rounded-lg">
          {['preview', 'raw'].map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={cn(
                "px-3 py-1 text-xs font-bold rounded-md transition-all capitalize",
                viewMode === mode ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
              )}
            >
              {mode === 'preview' ? 'Rendered' : 'Raw MD'}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Bar — fixed, never scrolls */}
      <StatsBar stats={stats} />

      {/* Scrollable content — ONLY this area scrolls */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {viewMode === 'preview' ? (
          <div className="p-6 md:p-10">
            {bookTitle && (
              <div className="text-center mb-10 pb-8 border-b border-slate-100">
                <h1 className="text-3xl font-black text-slate-900">{bookTitle}</h1>
                {bookAuthor && <p className="text-slate-500 mt-2 italic">by {bookAuthor}</p>}
              </div>
            )}
            <div className="prose prose-slate prose-sm sm:prose-base max-w-none">
              {parts.map((part, index) => (
                <PartCard
                  key={part.id}
                  part={part}
                  index={index}
                  total={parts.length}
                  onRemove={onRemovePart}
                  onMoveUp={() => moveUp(index)}
                  onMoveDown={() => moveDown(index)}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="p-4">
            <pre className="font-mono text-xs text-slate-600 whitespace-pre-wrap bg-slate-50 p-5 rounded-xl border border-slate-100 leading-relaxed">
              {combinedMarkdown}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}